import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Realtime as AblyRealtime } from 'ably';
import { useSelector } from 'react-redux';
import { getAblyToken } from '../store/actions/user';
import type { RootState } from '../store';
import debug from '../utilities/debug';

// Singleton — persists across component remounts for the lifetime of the session
let globalClient: AblyRealtime | null = null;
let globalInitialized = false;
// The account the client's token was minted for. The token's clientId embeds
// it, and Ably permanently fails a connection whose clientId changes on re-auth,
// so another account gets a new client rather than a refreshed token.
let globalAccountId: string | null = null;
// Bumped by teardown; an init still awaiting its token from an older generation
// has been superseded and must not install its client.
let globalGeneration = 0;

// Ably's error code for an attach the token's capability does not cover.
const CAPABILITY_DENIED = 40160;
// A channel still denied under a fresh token is outside the caller's access;
// wait this long before spending another token request on it.
const DENIED_RETRY_MS = 60_000;
// A screen's channels are denied within moments of each other; one token
// request covers them all.
const DENIED_BATCH_MS = 250;

/**
 * The token grants the exact plan and room channels the caller had when it was
 * minted, so a plan started or a room joined afterwards is denied until the
 * token is refreshed. Refresh once when that happens and re-attach the denied
 * channels; their listeners survive the failure, so subscribers pick up from
 * the re-attach.
 */
export function recoverDeniedChannels(client: AblyRealtime) {
  const channels = client.channels as any;
  const baseGet = channels.get.bind(channels);
  const watched = new WeakSet<object>();
  const denied = new Map<string, any>();
  const retriedAt = new Map<string, number>();
  let refreshing = false;
  let scheduled: ReturnType<typeof setTimeout> | null = null;

  // authorize() reconnects a closed client, so a client torn down for an
  // account switch or sign-out must not refresh or re-attach.
  const retired = () => ['closing', 'closed', 'failed'].includes(client.connection.state);
  const schedule = () => {
    if (!scheduled && denied.size) scheduled = setTimeout(refresh, DENIED_BATCH_MS);
  };
  const refresh = () => {
    scheduled = null;
    if (refreshing || !denied.size) return;
    if (retired()) { denied.clear(); return; }
    refreshing = true;
    const batch = new Map(denied);
    denied.clear();
    client.auth.authorize((err: any) => {
      refreshing = false;
      if (retired()) return;
      if (err) {
        debug.warn('Ably token refresh for denied channels failed', err?.message);
      } else {
        batch.forEach((channel, name) => {
          retriedAt.set(name, Date.now());
          channel.attach();
        });
      }
      // Channels denied while that token was in flight may postdate it.
      schedule();
    });
  };

  channels.get = (name: string, options?: any) => {
    const channel = baseGet(name, options);
    if (!watched.has(channel)) {
      watched.add(channel);
      channel.on('failed', (change: any) => {
        if (change?.reason?.code !== CAPABILITY_DENIED) return;
        if (Date.now() - (retriedAt.get(name) ?? 0) < DENIED_RETRY_MS) return;
        denied.set(name, channel);
        schedule();
      });
    } else if (channel.state === 'failed' && channel.errorReason?.code === CAPABILITY_DENIED) {
      // ably-js v1 drops subscribe() on a FAILED channel without keeping the
      // listener, so revive it for this caller; a repeat denial comes back
      // through the listener above.
      channel.attach();
    }
    return channel;
  };
}

interface AblyContextValue {
  client: AblyRealtime | null;
  connected: boolean;
}

const AblyContext = createContext<AblyContextValue>({ client: null, connected: false });

export const useAblyClient = () => useContext(AblyContext);

export const AblyProvider = ({ children }: { children: React.ReactNode }) => {
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const cognitoId = useSelector((state: RootState) => state.authentication.user?.cognito_id);
  const accountId = useSelector((state: RootState) => state.session.account?.account_id);

  const [connected, setConnected] = useState(() => globalClient?.connection?.state === 'connected');
  const isConfiguring = useRef(false);
  const connectionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCount = useRef(0);

  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeout.current) {
      clearTimeout(connectionTimeout.current);
      connectionTimeout.current = null;
    }
  }, []);

  // Close the session client and forget its account. Its connection handlers
  // check they still belong to globalClient, so a late 'closed' can't clobber
  // a successor.
  const teardown = useCallback(() => {
    if (retryTimeout.current) { clearTimeout(retryTimeout.current); retryTimeout.current = null; }
    clearConnectionTimeout();
    retryCount.current = 0;
    const client = globalClient;
    globalClient = null;
    globalInitialized = false;
    globalAccountId = null;
    globalGeneration += 1;
    isConfiguring.current = false;
    setConnected(false);
    try { client?.close(); } catch {}
  }, [clearConnectionTimeout]);

  const init = useCallback(async (tokenAccountId: string) => {
    if (globalInitialized || isConfiguring.current) return;
    isConfiguring.current = true;
    globalInitialized = true;
    globalAccountId = tokenAccountId;
    const generation = globalGeneration;

    debug.log('ably', 'Fetching Ably token for mobile');
    const result = await getAblyToken(tokenAccountId);
    if (generation !== globalGeneration) return;
    if (!result?.payload?.token) {
      debug.warn('Failed to get Ably token — real-time features offline', result?.message);
      globalInitialized = false;
      isConfiguring.current = false;
      // Retry with backoff (max 3 attempts) to recover from transient startup race conditions
      if (retryCount.current < 3) {
        const delay = Math.min(2000 * Math.pow(2, retryCount.current), 16000);
        retryCount.current += 1;
        retryTimeout.current = setTimeout(() => init(tokenAccountId), delay);
      }
      return;
    }
    retryCount.current = 0;

    const initialToken = result.payload.token;

    connectionTimeout.current = setTimeout(() => {
      debug.warn('Ably connection timeout after 5s — continuing offline');
      clearConnectionTimeout();
      if (globalClient && globalClient.connection.state !== 'connected') {
        try { globalClient.close(); } catch {}
        globalClient = null;
      }
      globalInitialized = false;
      isConfiguring.current = false;
    }, 5000);

    try {
      const client = new AblyRealtime({
        token: initialToken,
        tls: true,
        authCallback: async (_tokenParams: any, callback: any) => {
          debug.log('ably', 'Token refresh requested');
          const refreshed = await getAblyToken(tokenAccountId);
          if (refreshed?.payload?.token) {
            callback(null, refreshed.payload.token);
          } else {
            callback(new Error('Failed to refresh Ably token'), null);
          }
        },
      });
      recoverDeniedChannels(client);

      globalClient = client;

      client.connection.on('connected', () => {
        if (globalClient !== client) return;
        debug.log('ably', 'Connected');
        clearConnectionTimeout();
        isConfiguring.current = false;
        setConnected(true);
      });

      client.connection.on('failed', () => {
        if (globalClient !== client) return;
        debug.warn('Ably connection failed');
        clearConnectionTimeout();
        globalClient = null;
        globalInitialized = false;
        isConfiguring.current = false;
        setConnected(false);
      });

      client.connection.on('closed', () => {
        if (globalClient !== client) return;
        debug.log('ably', 'Connection closed');
        globalClient = null;
        globalInitialized = false;
        setConnected(false);
      });

      client.connection.on('disconnected', () => {
        if (globalClient !== client) return;
        debug.log('ably', 'Disconnected — will reconnect automatically');
        setConnected(false);
      });

      if (client.connection.state === 'connected') {
        clearConnectionTimeout();
        isConfiguring.current = false;
        setConnected(true);
      }
    } catch (err) {
      debug.error('Failed to initialize Ably client', err);
      clearConnectionTimeout();
      globalInitialized = false;
      isConfiguring.current = false;
    }
  }, [clearConnectionTimeout]);

  useEffect(() => {
    if (!authorized || !cognitoId || !accountId) return;

    // Switched accounts — the current token names the old one.
    if (globalInitialized && globalAccountId !== accountId) teardown();

    // Already initialized — sync state
    if (globalInitialized) {
      setConnected(globalClient?.connection?.state === 'connected');
      return;
    }

    init(accountId);

    return () => {
      // Don't close on unmount — singleton persists for the session
    };
  }, [authorized, cognitoId, accountId, init, teardown]);

  // Tear down on logout
  useEffect(() => {
    if (!authorized && globalInitialized) {
      debug.log('ably', 'Logged out — closing connection');
      teardown();
    }
  }, [authorized, teardown]);

  return (
    <AblyContext.Provider value={{ client: globalClient, connected }}>
      {children}
    </AblyContext.Provider>
  );
};

export default AblyContext;
